import React from 'react';
import type { Technician } from '@shared/types';
import { TechnicianManager } from '../components/TechnicianManager';
import { Users } from 'lucide-react';

interface TeamManagementPageProps {
    technicians: Technician[];
    setTechnicians: React.Dispatch<React.SetStateAction<Technician[]>>;
}

export const TeamManagementPage: React.FC<TeamManagementPageProps> = ({
    technicians,
    setTechnicians,
}) => {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-100">Gestion d'Équipe</h2>
                    <p className="text-sm text-gray-400">
                        {technicians.length} technicien{technicians.length !== 1 ? 's' : ''} dans l'équipe
                    </p>
                </div>
            </div>

            {/* Technician Manager */}
            <div className="glass-panel p-6">
                <TechnicianManager
                    technicians={technicians}
                    setTechnicians={setTechnicians}
                />
            </div>

            {/* Team Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Services breakdown */}
                <div className="glass-panel p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">Par Service</h3>
                    <div className="space-y-2">
                        {Object.entries(
                            technicians.reduce((acc, tech) => {
                                acc[tech.service] = (acc[tech.service] || 0) + 1;
                                return acc;
                            }, {} as Record<string, number>)
                        ).map(([service, count]) => (
                            <div key={service} className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">{service}</span>
                                <span className="font-medium text-gray-200">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Active assignments */}
                <div className="glass-panel p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">Chantiers Actifs</h3>
                    <div className="space-y-2">
                        {technicians.map(tech => {
                            const activeCount = tech.assignments.filter(
                                a => !a.status || a.status === 'pending' || a.status === 'in-progress'
                            ).length;
                            if (activeCount === 0) return null;
                            return (
                                <div key={tech.id} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: tech.color }}
                                        />
                                        <span className="text-gray-400">{tech.name}</span>
                                    </div>
                                    <span className="font-medium text-gray-200">{activeCount}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Total workload */}
                <div className="glass-panel p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">Charge Totale</h3>
                    <div className="text-center">
                        <div className="text-4xl font-bold text-primary mb-1">
                            {technicians.reduce((sum, tech) => sum + tech.assignments.length, 0)}
                        </div>
                        <div className="text-xs text-gray-400">chantiers assignés</div>
                    </div>
                </div>
            </div>

            {/* Team List */}
            <div className="glass-panel p-6">
                <h3 className="text-lg font-semibold text-gray-200 mb-4">Liste des Techniciens</h3>
                <div className="grid gap-3">
                    {technicians.map(tech => (
                        <div
                            key={tech.id}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm"
                                    style={{ backgroundColor: tech.color }}
                                >
                                    {tech.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-200">{tech.name}</div>
                                    <div className="text-xs text-gray-400">{tech.service}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                                <div className="text-gray-400">
                                    {tech.assignments.length} chantier{tech.assignments.length !== 1 ? 's' : ''}
                                </div>
                                <div
                                    className="px-2 py-1 rounded text-xs font-medium"
                                    style={{
                                        backgroundColor: `${tech.color}20`,
                                        color: tech.color
                                    }}
                                >
                                    {tech.service}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
